import { API_PATHS, type Notification } from '@uroute/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { invalidate, useApiQuery } from './useQuery';
import { makeItem, submitItems } from './queue';

const SEEN_KEY = 'uroute.driver.seenNotifications';

function normalize(raw: unknown): Notification[] {
  if (Array.isArray(raw)) return raw as Notification[];
  if (raw && typeof raw === 'object') {
    const items = (raw as { items?: unknown }).items;
    if (Array.isArray(items)) return items as Notification[];
  }
  return [];
}

function readSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const arr = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeSeen(ids: Iterable<string>): void {
  try {
    const all = new Set([...readSeen(), ...ids]);
    const arr = [...all].slice(-200);
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

/** Fire a system notification (permission already granted). */
export function showDeviceNotification(n: Pick<Notification, 'title' | 'body' | 'id'>, url?: string): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const note = new Notification(n.title, {
      body: n.body,
      tag: n.id,
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-192.png',
      data: { url },
    });
    note.onclick = () => {
      window.focus();
      if (url) window.location.assign(url);
      note.close();
    };
  } catch {
    /* Android requires a service worker notification ??? silently skip */
  }
}

/** Only ever called from a user gesture (button click). */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

export function notificationLink(n: Notification): string | undefined {
  if (n.jobId) return `/jobs/${n.jobId}`;
  if (n.link) return n.link;
  return undefined;
}

export interface NotificationsView {
  items: Notification[];
  unread: number;
  loading: boolean;
  error: string | null;
  offline: boolean;
  updatedAt: number | null;
  refresh: () => Promise<void>;
  markRead: (n: Notification) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export function useNotifications(): NotificationsView {
  const path = `${API_PATHS.notifications}?pageSize=50`;
  const query = useApiQuery<unknown>('notifications', path);
  const [overrides, setOverrides] = useState<ReadonlySet<string>>(() => new Set());

  const serverItems = useMemo(
    () => normalize(query.data).sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
    [query.data],
  );

  const items = useMemo(
    () =>
      serverItems.map((n) =>
        overrides.has(n.id) && !n.readAt ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n,
      ),
    [serverItems, overrides],
  );

  const unread = items.filter((n) => !n.readAt).length;

  /* Poll while the app is visible so badges stay honest. */
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) void query.refresh();
    }, 60_000);
    return () => window.clearInterval(id);
  }, [query.refresh]);

  /* Surface brand-new assignments as system notifications when hidden. */
  useEffect(() => {
    if (items.length === 0) return;
    const unseen = items.filter((n) => !n.readAt && !readSeen().has(n.id));
    if (unseen.length === 0) return;
    if (document.visibilityState === 'hidden') {
      for (const n of unseen.slice(0, 3)) showDeviceNotification(n, notificationLink(n));
    }
    writeSeen(unseen.map((n) => n.id));
  }, [items]);

  const markRead = useCallback(
    async (n: Notification) => {
      if (n.readAt) return;
      setOverrides((prev) => new Set(prev).add(n.id));
      const item = makeItem({
        kind: 'notification-read',
        label: `Read: ${n.title}`,
        path: `${API_PATHS.notifications}/${n.id}/read`,
      });
      const result = await submitItems([item]);
      if (result.status !== 'queued') invalidate('notifications');
    },
    [],
  );

  const markAllRead = useCallback(async () => {
    const unreadIds = items.filter((n) => !n.readAt).map((n) => n.id);
    if (unreadIds.length === 0) return;
    setOverrides((prev) => new Set([...prev, ...unreadIds]));
    const item = makeItem({
      kind: 'notification-read-all',
      label: 'Mark all notifications read',
      path: `${API_PATHS.notifications}/read-all`,
    });
    const result = await submitItems([item]);
    if (result.status !== 'queued') invalidate('notifications');
  }, [items]);

  return {
    items,
    unread,
    loading: query.loading,
    error: query.error,
    offline: query.offline,
    updatedAt: query.updatedAt,
    refresh: query.refresh,
    markRead,
    markAllRead,
  };
}
